/**
 * Settings → Pressure Testing → Rate card (PT-5, fieldiq-api#932 `/rates`).
 *
 * PT's own rate card — not the casing rate sheets, not the rentals price books.
 * One row prices one KEY for one customer (none = the company default) over a
 * date range. Two views of the same `GET /rates`:
 *   Company defaults   `customer_id=default`
 *   Customer overrides every row with a customer, optionally one customer's
 * A customer override beats the default when a ticket is priced; the API
 * resolves that, and nothing here works out which rate a ticket would get.
 *
 * SECTIONS PER KIND: tests (by test type), labour, mobilization, mileage,
 * standby, equipment (day and hour, by instrument kind or one instrument), and
 * consumables (name + unit). Each row: the price, its effective and expiry
 * days, and whether it is in force today — the API's `in_force`, against the
 * tenant's day.
 *
 * WRITES are PT_ADMIN (admin, office_staff), as is the read: anyone else gets
 * the API's refusal, shown as a refusal, never an empty card. Add sets the key;
 * Edit changes price, currency, dates and notes (the key is fixed —
 * `rate_key_immutable`); Retire ends a rate today (its expiry), which keeps the
 * row as history, and a rate not yet in effect is deleted instead. Overlap with
 * another live rate for the same key is the API's 409 `rate_overlap`, shown as
 * sent.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Archive, Loader2, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import {
  useCreatePtRate,
  useDeletePtRate,
  usePtInstruments,
  usePtRates,
  usePtTestTypes,
  useUpdatePtRate,
  type PtRateListParams,
} from '../../hooks/usePressureTesting';
import { formatCalendarDate } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminPt, PT_LINE_KIND_LABEL, ptKindLabel, ptRateText } from '../../lib/pressure-testing';
import { PT_INSTRUMENT_KINDS, PT_RATE_KINDS, type PtRate, type PtRateCreate, type PtRateKind } from '../../types/pressure-testing';
import { PT_INPUT, PT_SECONDARY, PtDialog, PtField } from './PtBits';
import { FormError, ptWriteError, usePtCustomers } from './PtJobDialogs';
import { ptAmountFromInput } from './PtTicketLineForms';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-60';
const day = (v: string | null | undefined) => formatCalendarDate(v) ?? '—';

/** The card's sections, in the order an office reads a ticket. Equipment day and hour share one. */
const SECTIONS: { key: string; title: string; kinds: PtRateKind[] }[] = [
  { key: 'test', title: 'Tests', kinds: ['test'] },
  { key: 'labour', title: 'Labour', kinds: ['labour_hour'] },
  { key: 'mobilization', title: 'Mobilization', kinds: ['mobilization'] },
  { key: 'mileage', title: 'Mileage', kinds: ['mileage_km'] },
  { key: 'standby', title: 'Standby', kinds: ['standby_hour'] },
  { key: 'equipment', title: 'Equipment', kinds: ['equipment_day', 'equipment_hour'] },
  { key: 'consumable', title: 'Consumables', kinds: ['consumable'] },
];

/** What a row prices, in words: the test type, the equipment, the consumable, or the kind itself. */
export function ptRateItem(r: PtRate): string {
  if (r.kind === 'test') return r.test_type_name || '—';
  if (r.kind === 'equipment_day' || r.kind === 'equipment_hour') {
    const what = r.equipment_name || (r.instrument_kind ? `Any ${ptKindLabel(r.instrument_kind).toLowerCase()}` : '—');
    return `${what} — per ${r.kind === 'equipment_day' ? 'day' : 'hour'}`;
  }
  if (r.kind === 'consumable') return r.consumable_name ? `${r.consumable_name}${r.unit ? ` (${r.unit})` : ''}` : '—';
  return PT_LINE_KIND_LABEL[r.kind as PtRateKind] ?? '—';
}

// ─── Add ─────────────────────────────────────────────────────────────────────

function AddRateDialog({
  onClose,
  today,
  currency,
  presetCustomerId,
}: {
  onClose: () => void;
  today: string | null;
  currency: string | null;
  presetCustomerId: string;
}) {
  const create = useCreatePtRate();
  const customers = usePtCustomers(true);
  const testTypes = usePtTestTypes();
  const instruments = usePtInstruments(undefined);
  const [kind, setKind] = useState<PtRateKind>('test');
  const [customerId, setCustomerId] = useState(presetCustomerId);
  const [testTypeId, setTestTypeId] = useState('');
  const [equipment, setEquipment] = useState('');
  const [consumable, setConsumable] = useState('');
  const [unit, setUnit] = useState('');
  const [rate, setRate] = useState('');
  const [cur, setCur] = useState(currency === 'USD' ? 'USD' : 'CAD');
  const [effective, setEffective] = useState(today ?? '');
  const [expiry, setExpiry] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isEq = kind === 'equipment_day' || kind === 'equipment_hour';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const r = ptAmountFromInput(rate);
    if (r === null || Number.isNaN(r)) return setError('Enter the rate as a number of 0 or more, with at most 2 decimals. 0 is "No charge".');
    const body: PtRateCreate = { kind, rate: r, currency: cur as 'CAD' | 'USD' };
    if (customerId) body.customer_id = customerId;
    if (kind === 'test') {
      if (!testTypeId) return setError('Choose the test type.');
      body.test_type_id = testTypeId;
    }
    if (isEq) {
      if (!equipment) return setError('Choose an instrument kind or one instrument.');
      if (equipment.startsWith('kind:')) body.instrument_kind = equipment.slice(5);
      else body.equipment_id = equipment.slice(3);
    }
    if (kind === 'consumable') {
      if (!consumable.trim() || !unit.trim()) return setError('A consumable needs a name and a unit (e.g. Glycol, L).');
      body.consumable_name = consumable.trim();
      body.unit = unit.trim();
    }
    if (effective) body.effective_date = effective;
    if (expiry) body.expiry_date = expiry;
    if (notes.trim()) body.notes = notes.trim();
    create.mutate(body, {
      onSuccess: () => {
        toast.success('Rate added');
        onClose();
      },
      onError: (err) => setError(ptWriteError(err, 'The rate was not added.').message),
    });
  };

  return (
    <PtDialog
      open
      title="Add rate"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className={PT_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="pt-add-rate" className={PRIMARY} disabled={create.isPending} data-testid="pt-rate-add-submit">
            {create.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Add rate
          </button>
        </>
      }
    >
      <form id="pt-add-rate" onSubmit={submit} className="space-y-4" noValidate>
        <FormError message={error} />
        <div className="grid gap-3 sm:grid-cols-2">
          <PtField label="What it prices">
            <select className={PT_INPUT} value={kind} onChange={(e) => setKind(e.target.value as PtRateKind)} data-testid="pt-rate-kind">
              {PT_RATE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {PT_LINE_KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </PtField>
          <PtField label="For" hint="A customer's rate beats the company default for that customer.">
            <select className={PT_INPUT} value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="pt-rate-customer">
              <option value="">Company default (every customer)</option>
              {(customers.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || '—'}
                </option>
              ))}
            </select>
          </PtField>
          {kind === 'test' && (
            <PtField label="Test type">
              <select className={PT_INPUT} value={testTypeId} onChange={(e) => setTestTypeId(e.target.value)} data-testid="pt-rate-test-type">
                <option value="">{testTypes.isLoading ? 'Loading…' : 'Choose a test type'}</option>
                {(testTypes.data?.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.is_active ? '' : ' (inactive)'}
                  </option>
                ))}
              </select>
            </PtField>
          )}
          {isEq && (
            <PtField label="Equipment" hint="One instrument beats a rate for its kind.">
              <select className={PT_INPUT} value={equipment} onChange={(e) => setEquipment(e.target.value)}>
                <option value="">Choose</option>
                <optgroup label="Any instrument of a kind">
                  {PT_INSTRUMENT_KINDS.map((k) => (
                    <option key={k} value={`kind:${k}`}>
                      {ptKindLabel(k)}
                    </option>
                  ))}
                </optgroup>
                {(instruments.data?.data ?? []).length > 0 && (
                  <optgroup label="One instrument">
                    {(instruments.data?.data ?? []).map((i) => (
                      <option key={i.id} value={`eq:${i.id}`}>
                        {i.name}
                        {i.serial_number ? ` · ${i.serial_number}` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </PtField>
          )}
          {kind === 'consumable' && (
            <>
              <PtField label="Consumable">
                <input className={PT_INPUT} value={consumable} onChange={(e) => setConsumable(e.target.value)} maxLength={100} placeholder="Glycol" data-testid="pt-rate-consumable" />
              </PtField>
              <PtField label="Unit">
                <input className={PT_INPUT} value={unit} onChange={(e) => setUnit(e.target.value)} maxLength={20} placeholder="L" data-testid="pt-rate-unit" />
              </PtField>
            </>
          )}
          <PtField label="Rate" hint="0 is No charge.">
            <input className={PT_INPUT} inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="0.00" data-testid="pt-rate-value" />
          </PtField>
          <PtField label="Currency">
            <select className={PT_INPUT} value={cur} onChange={(e) => setCur(e.target.value)}>
              <option value="CAD">CAD</option>
              <option value="USD">USD</option>
            </select>
          </PtField>
          <PtField label="Effective">
            <input type="date" className={PT_INPUT} value={effective} onChange={(e) => setEffective(e.target.value)} />
          </PtField>
          <PtField label="Expires" hint="Blank: open-ended.">
            <input type="date" className={PT_INPUT} value={expiry} onChange={(e) => setExpiry(e.target.value)} />
          </PtField>
        </div>
        <PtField label="Notes">
          <textarea className={`${PT_INPUT} h-20 py-2`} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
        </PtField>
      </form>
    </PtDialog>
  );
}

// ─── Edit / retire ───────────────────────────────────────────────────────────

function EditRateDialog({ rate, onClose }: { rate: PtRate; onClose: () => void }) {
  const update = useUpdatePtRate();
  const [value, setValue] = useState(String(rate.rate));
  const [cur, setCur] = useState(rate.currency === 'USD' ? 'USD' : 'CAD');
  const [effective, setEffective] = useState(rate.effective_date ?? '');
  const [expiry, setExpiry] = useState(rate.expiry_date ?? '');
  const [notes, setNotes] = useState(rate.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const r = ptAmountFromInput(value);
    if (r === null || Number.isNaN(r)) return setError('Enter the rate as a number of 0 or more, with at most 2 decimals.');
    if (!effective) return setError('The effective day is required.');
    // Only what changed: the API keeps whatever it is not handed.
    const body: Record<string, unknown> = {};
    if (r !== rate.rate) body.rate = r;
    if (cur !== rate.currency) body.currency = cur;
    if (effective !== rate.effective_date) body.effective_date = effective;
    if ((expiry || null) !== rate.expiry_date) body.expiry_date = expiry || null;
    if ((notes.trim() || null) !== rate.notes) body.notes = notes.trim() || null;
    if (!Object.keys(body).length) return onClose();
    update.mutate(
      { id: rate.id, body },
      {
        onSuccess: () => {
          toast.success('Rate saved');
          onClose();
        },
        onError: (err) => setError(ptWriteError(err, 'The rate was not saved.').message),
      },
    );
  };

  return (
    <PtDialog
      open
      title={`Edit rate — ${ptRateItem(rate)}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={PT_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="pt-edit-rate" className={PRIMARY} disabled={update.isPending} data-testid="pt-rate-edit-save">
            {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Save
          </button>
        </>
      }
    >
      <form id="pt-edit-rate" onSubmit={submit} className="space-y-4" noValidate>
        <p className="text-sm text-fieldiq-text">
          {rate.customer_name ? `${rate.customer_name}'s rate.` : 'The company default.'} What it prices cannot change; retire it and add a new one instead.
        </p>
        <FormError message={error} />
        <div className="grid gap-3 sm:grid-cols-2">
          <PtField label={`Rate${rate.unit ? ` per ${rate.unit}` : ''}`} hint="0 is No charge.">
            <input className={PT_INPUT} inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} data-testid="pt-rate-edit-value" autoFocus />
          </PtField>
          <PtField label="Currency">
            <select className={PT_INPUT} value={cur} onChange={(e) => setCur(e.target.value)}>
              <option value="CAD">CAD</option>
              <option value="USD">USD</option>
            </select>
          </PtField>
          <PtField label="Effective">
            <input type="date" className={PT_INPUT} value={effective} onChange={(e) => setEffective(e.target.value)} />
          </PtField>
          <PtField label="Expires" hint="Blank: open-ended.">
            <input type="date" className={PT_INPUT} value={expiry} onChange={(e) => setExpiry(e.target.value)} />
          </PtField>
        </div>
        <PtField label="Notes">
          <textarea className={`${PT_INPUT} h-20 py-2`} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
        </PtField>
      </form>
    </PtDialog>
  );
}

/**
 * Retire: a rate in effect ends today (expiry = the tenant's today), so tickets
 * priced from it keep their history. A rate not yet in effect has no history to
 * keep and is deleted.
 */
function RetireRateDialog({ rate, today, onClose }: { rate: PtRate; today: string | null; onClose: () => void }) {
  const update = useUpdatePtRate();
  const del = useDeletePtRate();
  const [error, setError] = useState<string | null>(null);
  const future = !!today && rate.effective_date > today;
  const pending = update.isPending || del.isPending;

  const confirm = () => {
    setError(null);
    const done = {
      onSuccess: () => {
        toast.success(future ? 'Rate deleted' : 'Rate retired');
        onClose();
      },
      onError: (err: unknown) => setError(ptWriteError(err, 'The rate was not retired.').message),
    };
    if (future) del.mutate({ id: rate.id }, done);
    else if (today) update.mutate({ id: rate.id, body: { expiry_date: today } }, done);
    else setError("The tenant's day did not arrive, so the rate cannot be ended today.");
  };

  return (
    <PtDialog
      open
      title={`Retire ${ptRateItem(rate)}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={PT_SECONDARY} onClick={onClose}>
            Keep it
          </button>
          <button type="button" className={PRIMARY} onClick={confirm} disabled={pending} data-testid="pt-rate-retire-confirm">
            {pending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {future ? 'Delete rate' : 'Retire rate'}
          </button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-fieldiq-text">
        <p>
          {future
            ? `This rate starts ${day(rate.effective_date)} and has not been used; it will be deleted.`
            : `This rate stops applying after ${day(today)}. Tickets already priced from it keep their price.`}
        </p>
        <FormError message={error} />
      </div>
    </PtDialog>
  );
}

// ─── The card ────────────────────────────────────────────────────────────────

function RateRows({
  rates,
  showCustomer,
  canWrite,
  onEdit,
  onRetire,
}: {
  rates: PtRate[];
  showCustomer: boolean;
  canWrite: boolean;
  onEdit: (r: PtRate) => void;
  onRetire: (r: PtRate) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] text-left">
        <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
          <tr className="border-l-4 border-l-transparent">
            <th scope="col" className="px-4 py-2">Item</th>
            {showCustomer && <th scope="col" className="px-4 py-2">Customer</th>}
            <th scope="col" className="px-4 py-2 text-right">Rate</th>
            <th scope="col" className="px-4 py-2">Effective</th>
            <th scope="col" className="px-4 py-2">Expires</th>
            <th scope="col" className="w-[88px] px-4 py-2" />
          </tr>
        </thead>
        <tbody>
          {rates.map((r) => (
            <tr
              key={r.id}
              data-testid="pt-rate-row"
              className={`border-t border-l-4 border-fieldiq-border ${r.in_force ? 'border-l-fi-green' : 'border-l-fi-steel'}`}
            >
              <td className="px-4 py-2.5">
                <p className="text-sm font-semibold text-white">{ptRateItem(r)}</p>
                {r.notes && <p className="mt-0.5 text-xs text-fieldiq-muted">{r.notes}</p>}
              </td>
              {showCustomer && <td className="px-4 py-2.5 text-sm text-fieldiq-text">{r.customer_name || '—'}</td>}
              <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-sm tabular-nums text-white" data-testid="pt-rate-value-cell">
                {r.rate === 0 ? 'No charge' : ptRateText(r.rate, r.currency, r.unit)}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-sm text-fieldiq-text">{day(r.effective_date)}</td>
              <td className="whitespace-nowrap px-4 py-2.5 text-sm text-fieldiq-text">
                {r.expiry_date ? day(r.expiry_date) : 'Open-ended'}
                {!r.in_force && <span className="ml-2 text-xs text-fieldiq-muted">not in force</span>}
              </td>
              <td className="px-4 py-2.5">
                {canWrite && (
                  <div className="flex justify-end gap-1">
                    <button
                      type="button"
                      className="inline-flex size-8 items-center justify-center rounded-fi-control text-fieldiq-muted transition hover:bg-fieldiq-dark/60 hover:text-fieldiq-text"
                      aria-label={`Edit ${ptRateItem(r)}`}
                      onClick={() => onEdit(r)}
                    >
                      <Pencil className="size-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="inline-flex size-8 items-center justify-center rounded-fi-control text-fieldiq-muted transition hover:bg-fieldiq-dark/60 hover:text-fieldiq-text"
                      aria-label={`Retire ${ptRateItem(r)}`}
                      onClick={() => onRetire(r)}
                      data-testid="pt-rate-retire"
                    >
                      <Archive className="size-4" aria-hidden="true" />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PtRateCard() {
  const { user } = useAuth();
  const canWrite = canAdminPt(user?.role);
  const [view, setView] = useState<'default' | 'customer'>('default');
  const [customerId, setCustomerId] = useState('');
  const [includeExpired, setIncludeExpired] = useState(false);
  const [dialog, setDialog] = useState<{ kind: 'add' } | { kind: 'edit'; rate: PtRate } | { kind: 'retire'; rate: PtRate } | null>(null);
  const customers = usePtCustomers(view === 'customer');

  const params: PtRateListParams = useMemo(
    () => ({
      ...(view === 'default' ? { customer_id: 'default' } : customerId ? { customer_id: customerId } : {}),
      ...(includeExpired ? { include_expired: 'true' as const } : {}),
    }),
    [view, customerId, includeExpired],
  );
  const query = usePtRates(params);
  const failure = queryLoadFailure(query);
  const meta = query.data?.meta;
  // The overrides view of "every customer" is every row with a customer.
  const rows = (query.data?.data ?? []).filter((r) => (view === 'default' ? !r.customer_id : !!r.customer_id));

  useEffect(() => {
    if (view === 'default') setCustomerId('');
  }, [view]);

  const tab = (key: 'default' | 'customer', label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={view === key}
      onClick={() => setView(key)}
      data-testid={`pt-rate-view-${key}`}
      className={`border-b-2 px-1 pb-2 text-sm font-bold transition ${
        view === key ? 'border-fi-amber text-fieldiq-text' : 'border-transparent text-fieldiq-muted hover:text-fieldiq-text'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="pt-rate-card">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div role="tablist" aria-label="Rate card view" className="flex gap-5 border-b border-fieldiq-border">
          {tab('default', 'Company defaults')}
          {tab('customer', 'Customer overrides')}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {view === 'customer' && (
            <select className={`${PT_INPUT} w-56`} value={customerId} onChange={(e) => setCustomerId(e.target.value)} aria-label="Customer" data-testid="pt-rate-customer-filter">
              <option value="">Every customer</option>
              {(customers.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || '—'}
                </option>
              ))}
            </select>
          )}
          <label className="flex items-center gap-2 text-sm text-fieldiq-text">
            <input type="checkbox" className="size-4 accent-fieldiq-accent" checked={includeExpired} onChange={(e) => setIncludeExpired(e.target.checked)} />
            Show expired
          </label>
          {canWrite && (
            <button type="button" className={PRIMARY} onClick={() => setDialog({ kind: 'add' })} data-testid="pt-rate-add">
              <Plus className="size-4" aria-hidden="true" />
              Add rate
            </button>
          )}
        </div>
      </div>

      <p className="text-xs text-fieldiq-muted">
        {view === 'default'
          ? 'What every customer is charged unless they have a rate of their own. A ticket line with no rate here, and no customer rate, reads "No rate" until the office prices it.'
          : "A customer's own rate beats the company default for that customer."}
        {canWrite ? '' : ' Only an admin or office staff can change the rate card.'}
      </p>

      {query.isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading the rate card" />
        </div>
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the rate card" />
      ) : (
        SECTIONS.map((s) => {
          const list = rows.filter((r) => s.kinds.includes(r.kind as PtRateKind));
          return (
            <section key={s.key} className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card" data-testid={`pt-rate-section-${s.key}`}>
              <div className="border-b border-fieldiq-border px-4 py-2.5">
                <h3 className="text-sm font-extrabold text-white">
                  {s.title} <span className="font-normal text-fieldiq-muted">({list.length})</span>
                </h3>
              </div>
              {list.length === 0 ? (
                <p className="px-4 py-3 text-sm text-fieldiq-text">
                  {view === 'default' ? `No company ${s.title.toLowerCase()} rate${includeExpired ? '' : ' in force or upcoming'}.` : `No customer ${s.title.toLowerCase()} overrides.`}
                </p>
              ) : (
                <RateRows
                  rates={list}
                  showCustomer={view === 'customer'}
                  canWrite={canWrite}
                  onEdit={(r) => setDialog({ kind: 'edit', rate: r })}
                  onRetire={(r) => setDialog({ kind: 'retire', rate: r })}
                />
              )}
            </section>
          );
        })
      )}

      {dialog?.kind === 'add' && (
        <AddRateDialog
          onClose={() => setDialog(null)}
          today={meta?.today ?? null}
          currency={meta?.currency ?? null}
          presetCustomerId={view === 'customer' ? customerId : ''}
        />
      )}
      {dialog?.kind === 'edit' && <EditRateDialog rate={dialog.rate} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'retire' && <RetireRateDialog rate={dialog.rate} today={meta?.today ?? null} onClose={() => setDialog(null)} />}
    </div>
  );
}
